// Schema and mapper for Salesforce Project_Objective__c to UI shape

export function fromSalesforceProjectObjective(sfRecord) {
  const poData = {
    info: {
      name: "",
      contributorFacingName: "",
      projectId: "",
      projectName: "",
      dateStart: "",
      dateEnd: "",
      totalTargetProductivityHours: "",
      poIdForReports: "",
      appendToObjectiveName: "",
    },
    requirements: {
      country: "",
      workType: "",
      excludeStates: false,
      currentlyResidingUsState: [],
      additionalManualMatchRequired: false,
      capDailyHoursSubmitted: false,
      language: "",
      dialectCountry: "",
      languageSkillLevel: "",
      fluencyType: "",
      listOfCountries: [],
      includeInCountryList: false,
      degreeRequirement: "",
    },
    funnelSplits: {
      funnelA: null,
      funnelB: null,
      funnelC: null,
      funnelD: null,
      funnelE: null,
    },
    targets: {
      productivityTargetType: "",
      weeklyTargetProductionHours: "",
      weeklyTargetProductionUnits: "",
      unitPerHour: "",
      targetProductionUnits: "",
      targetProductionHours: "",
    },
    diversity: {
      minimumContributors: null,
    },
    stats: {
      totalApplied: 0,
      totalQualified: 0,
      invitedAvailableContributors: 0,
      appReceived: 0,
      matchedContributors: 0,
      removed: 0,
      qualifiedContributors: 0,
      activeContributors: 0,
      productionContributors: 0,
    },
    rates: {
      minimumRate: "",
      nonUSFairpayRate: "",
      nonUSProjectPayRate: "",
      projectRate: "",
      projectObjectivePayRate: "",
      projectPayRate: "",
      clientProjectTypePayRate: "",
      clientPayRate: "",
      projectTypePayRate: "",
      payRateAboveFairpay: "",
      flatPayRate: "",
      projectIncentiveRate: "",
      maximumRate: "",
      hidePayRate: false,
    },
    legacy: {
      acProjectId: "",
      eventLogsAcProjectId: "",
      rosterUserGroupIdExclusive: "",
      rosterUserGroupId: "",
      rosterUserGroupName: "",
    },
    admin: {
      overrideDNH: false,
      displayLeverPostErrorMessage: false,
      createInvites: false,
      status: "",
      countryLocale: "",
      languageLocale: "",
      dialectLocale: "",
      refresh: false,
      mercuryProjectObjectiveType: "",
    },
    sys: {
      createdById: "",
      lastModifiedById: "",
    },
  };

  if (!sfRecord || typeof sfRecord !== "object") return { id: null, ...poData };

  // Info
  poData.info.name = sfRecord.Name || "";
  poData.info.contributorFacingName =
    sfRecord.Contributor_Facing_Project_Name__c || "";
  poData.info.objectiveDescription =
    sfRecord.Project_Objective_Description__c || "";
  poData.info.projectId =
    (sfRecord.Project__r && sfRecord.Project__r.Id) ||
    sfRecord.Project__c ||
    "";
  poData.info.projectName =
    (sfRecord.Project__r && sfRecord.Project__r.Name) || "";
  poData.info.dateStart = sfRecord.Date_Start__c || "";
  poData.info.dateEnd = sfRecord.Date_End__c || "";
  poData.info.totalTargetProductivityHours =
    sfRecord.Total_Target_Productivity_Hours__c ?? "";
  poData.info.poIdForReports =
    sfRecord.Project_Objective_ID_for_Reports__c || "";
  poData.info.appendToObjectiveName =
    sfRecord.Append_to_Objective_Name__c || "";

  // Requirements
  poData.requirements.country = sfRecord.Country__c || "";
  poData.requirements.workType = sfRecord.Work_Type__c || "";
  poData.requirements.additionalManualMatchRequired = Boolean(
    sfRecord.Additional_Manual_Match_Required__c,
  );
  poData.requirements.capDailyHoursSubmitted = Boolean(
    sfRecord.Cap_Daily_Hours_Submitted__c,
  );
  poData.requirements.language = sfRecord.Language__c || "";
  poData.requirements.dialectCountry = sfRecord.Dialect_Country__c || "";
  poData.requirements.languageSkillLevel =
    sfRecord.Language_Skill_Level__c || "";
  poData.requirements.fluencyType = sfRecord.Fluency_Type__c || "";
  poData.requirements.includeInCountryList =
    sfRecord.Include_in_Country_List__c || false;
  poData.requirements.listOfCountries = sfRecord.List_of_Countries__c || "";

  poData.requirements.excludeStates = Boolean(sfRecord.Exclude_States__c);

  poData.requirements.currentlyResidingUsState =
    sfRecord.Currently_Residing_US_State__c || "";

  poData.requirements.degreeRequirement = sfRecord.Degree_Requirement__c || "";

  // Funnel Splits
  poData.funnelSplits.funnelA = sfRecord.to_use_Funnel_A__c ?? null;
  poData.funnelSplits.funnelB = sfRecord.to_use_Funnel_B__c ?? null;
  poData.funnelSplits.funnelC = sfRecord.to_use_Funnel_C__c ?? null;
  poData.funnelSplits.funnelD = sfRecord.to_use_Funnel_D__c ?? null;
  poData.funnelSplits.funnelE = sfRecord.to_use_Funnel_E__c ?? null;

  // Targets
  poData.targets.productivityTargetType =
    sfRecord.Productivity_Target_Type__c || "";
  poData.targets.weeklyTargetProductionHours =
    sfRecord.Weekly_Target_Production_Hours_Calc__c ?? "";
  poData.targets.weeklyTargetProductionUnits =
    sfRecord.Weekly_Target_Production_Units_Calc__c ?? "";
  poData.targets.unitPerHour = sfRecord.Unit_per_Hour__c ?? "";
  poData.targets.targetProductionUnits =
    sfRecord.Target_Production_Units__c ?? "";
  poData.targets.targetProductionHours =
    sfRecord.Target_Production_Hours__c ?? "";
  poData.targets.productionPaymentThreshold =
    sfRecord.Production_Payment_Threshold__c ?? "";
  poData.targets.qualityPaymentThreshold =
    sfRecord.Quality_Payment_Threshold__c ?? "";
  // Diversity
  poData.diversity.minimumContributors =
    sfRecord.Minimum_Contributors__c ?? null;

  // Stats
  poData.stats.totalApplied = sfRecord.Total_Applied__c ?? 0;
  poData.stats.totalQualified = sfRecord.Total_Qualified__c ?? 0;
  poData.stats.invitedAvailableContributors =
    sfRecord.Invited_Available_Contributors__c ?? 0;
  poData.stats.appReceived = sfRecord.App_Received__c ?? 0;
  poData.stats.matchedContributors = sfRecord.Matched_Contributors__c ?? 0;
  poData.stats.removed = sfRecord.Removed__c ?? 0;
  poData.stats.qualifiedContributors = sfRecord.Qualified_Contributors__c ?? 0;
  poData.stats.activeContributors = sfRecord.Active_Contributors__c ?? 0;
  poData.stats.productionContributors =
    sfRecord.Production_Contributors__c ?? 0;

  // Rates
  poData.rates.minimumRate = sfRecord.Minimum_Rate__c ?? "";
  poData.rates.nonUSFairpayRate = sfRecord.Non_US_Fairpay_Rate__c ?? "";
  poData.rates.nonUSProjectPayRate = sfRecord.Non_US_Project_Pay_rate__c ?? "";
  poData.rates.projectRate = sfRecord.Project_Rate__c ?? "";
  poData.rates.projectObjectivePayRate =
    sfRecord.Project_Objective_Pay_Rate__c ?? "";
  poData.rates.projectPayRate = sfRecord.Project_Pay_Rate__c ?? "";
  poData.rates.clientProjectTypePayRate =
    sfRecord.Client_Project_Type_Pay_Rate__c ?? "";
  poData.rates.clientPayRate = sfRecord.Client_Pay_Rate__c ?? "";
  poData.rates.projectTypePayRate = sfRecord.Project_Type_Pay_Rate__c ?? "";
  poData.rates.payRateAboveFairpay = sfRecord.Pay_Rate_Above_Fairpay__c ?? "";
  poData.rates.flatPayRate = sfRecord.Flat_Pay_Rate__c ?? "";
  poData.rates.projectIncentiveRate = sfRecord.Project_Incentive_Rate__c ?? "";
  poData.rates.hidePayRate = sfRecord.Hide_Rate__c ?? "";
  poData.rates.maximumRate = sfRecord.Maximum_Rate__c ?? "";

  // Legacy / AC
  poData.legacy.acProjectId = sfRecord.AC_Project_ID__c || "";
  poData.legacy.eventLogsAcProjectId =
    sfRecord.Event_Logs_AC_Project_ID__c || "";
  poData.legacy.rosterUserGroupIdExclusive =
    sfRecord.Roster_User_Group_ID_Exclusive__c || "";
  poData.legacy.rosterUserGroupId = sfRecord.Roster_User_Group_ID__c || "";
  poData.legacy.rosterUserGroupName = sfRecord.Roster_User_Group_Name__c || "";

  // Admin
  poData.admin.overrideDNH = Boolean(sfRecord.Override_DNH__c);
  poData.admin.displayLeverPostErrorMessage = Boolean(
    sfRecord.Display_Lever_Post_Error_Message__c,
  );
  poData.admin.createInvites = Boolean(sfRecord.Create_Invites__c);
  poData.admin.status = sfRecord.Status__c || "";
  poData.admin.countryLocale = sfRecord.Country_Locale__c || "";
  poData.admin.languageLocale = sfRecord.Language_Locale__c || "";
  poData.admin.dialectLocale = sfRecord.Dialect_Locale__c || "";
  poData.admin.refresh = Boolean(sfRecord.Refresh__c);
  poData.admin.mercuryProjectObjectiveType =
    sfRecord.Mercury_Project_Objective_Type__c || "";

  // Sys
  poData.sys.createdById = sfRecord.CreatedById || "";
  poData.sys.lastModifiedById = sfRecord.LastModifiedById || "";

  const id = sfRecord.Id || null;
  return { id, ...poData };
}

// Mapper for PATCH/UPDATE back to Salesforce fields
export function toSalesforceProjectObjective(uiRecord) {
  const rec = {};
  if (!uiRecord || typeof uiRecord !== "object") return rec;
  const info = uiRecord.info || {};
  const requirements = uiRecord.requirements || {};
  const funnelSplits = uiRecord.funnelSplits || {};
  const targets = uiRecord.targets || {};
  const diversity = uiRecord.diversity || {};
  const stats = uiRecord.stats || {};
  const rates = uiRecord.rates || {};
  const legacy = uiRecord.legacy || {};
  const admin = uiRecord.admin || {};
  if (uiRecord.id) rec.Id = uiRecord.id;
  // Info
  if (info.name !== undefined) rec.Name = info.name || null;
  if (info.contributorFacingName !== undefined)
    rec.Contributor_Facing_Project_Name__c = info.contributorFacingName || null;
  if (info.objectiveDescription !== undefined)
    rec.Project_Objective_Description__c = info.objectiveDescription || "";
  if (info.projectId !== undefined) rec.Project__c = info.projectId || null;
  if (info.dateStart !== undefined) rec.Date_Start__c = info.dateStart || null;
  if (info.dateEnd !== undefined) rec.Date_End__c = info.dateEnd || null;
  if (info.totalTargetProductivityHours !== undefined)
    rec.Total_Target_Productivity_Hours__c = info.totalTargetProductivityHours;
  if (info.poIdForReports !== undefined)
    rec.Project_Objective_ID_for_Reports__c = info.poIdForReports || null;
  if (info.appendToObjectiveName !== undefined)
    rec.Append_to_Objective_Name__c = info.appendToObjectiveName || null;
  // Requirements
  if (requirements.country !== undefined)
    rec.Country__c = requirements.country || null;
  if (
    requirements.country === undefined &&
    requirements.includeInCountryList !== undefined
  )
    rec.Include_in_Country_List__c = requirements.includeInCountryList || false;
  if (
    requirements.country === undefined &&
    requirements.listOfCountries !== undefined
  )
    rec.List_of_Countries__c =
      Array.isArray(requirements.listOfCountries) &&
      requirements.listOfCountries.length > 0
        ? requirements.listOfCountries.join(";")
        : "";
  if (requirements.workType !== undefined)
    rec.Work_Type__c = requirements.workType || null;
  if (requirements.additionalManualMatchRequired !== undefined)
    rec.Additional_Manual_Match_Required__c = Boolean(
      requirements.additionalManualMatchRequired,
    );
  if (requirements.capDailyHoursSubmitted !== undefined)
    rec.Cap_Daily_Hours_Submitted__c = Boolean(
      requirements.capDailyHoursSubmitted,
    );
  if (requirements.language !== undefined)
    rec.Language__c = requirements.language || null;
  if (requirements.dialectCountry !== undefined)
    rec.Dialect_Country__c = requirements.dialectCountry || null;
  if (requirements.languageSkillLevel !== undefined)
    rec.Language_Skill_Level__c = requirements.languageSkillLevel || null;
  if (requirements.fluencyType !== undefined)
    rec.Fluency_Type__c = requirements.fluencyType || null;
  if (requirements.excludeStates !== undefined)
    rec.Exclude_States__c = Boolean(requirements.excludeStates);
  if (requirements.currentlyResidingUsState !== undefined) {
    rec.Currently_Residing_US_State__c =
      Array.isArray(requirements.currentlyResidingUsState) &&
      requirements.currentlyResidingUsState.length > 0
        ? requirements.currentlyResidingUsState.join(";")
        : "";
  }
  if (requirements.degreeRequirement !== undefined) {
    rec.Degree_Requirement__c = requirements.degreeRequirement;
  }

  // Funnel splits
  if (funnelSplits.funnelA !== undefined)
    rec.to_use_Funnel_A__c = funnelSplits.funnelA;
  if (funnelSplits.funnelB !== undefined)
    rec.to_use_Funnel_B__c = funnelSplits.funnelB;
  if (funnelSplits.funnelC !== undefined)
    rec.to_use_Funnel_C__c = funnelSplits.funnelC;
  if (funnelSplits.funnelD !== undefined)
    rec.to_use_Funnel_D__c = funnelSplits.funnelD;
  if (funnelSplits.funnelE !== undefined)
    rec.to_use_Funnel_E__c = funnelSplits.funnelE;
  // Targets
  if (targets.productivityTargetType !== undefined)
    rec.Productivity_Target_Type__c = targets.productivityTargetType || null;
  if (targets.weeklyTargetProductionHours !== undefined)
    rec.Weekly_Target_Production_Hours_Calc__c =
      targets.weeklyTargetProductionHours;
  if (targets.weeklyTargetProductionUnits !== undefined)
    rec.Weekly_Target_Production_Units_Calc__c =
      targets.weeklyTargetProductionUnits;
  if (targets.unitPerHour !== undefined)
    rec.Unit_per_Hour__c = targets.unitPerHour;
  if (targets.targetProductionUnits !== undefined)
    rec.Target_Production_Units__c = targets.targetProductionUnits;
  if (targets.targetProductionHours !== undefined)
    rec.Target_Production_Hours__c = targets.targetProductionHours;
  if (targets.productionPaymentThreshold !== undefined)
    rec.Production_Payment_Threshold__c = targets.productionPaymentThreshold;
  if (targets.qualityPaymentThreshold !== undefined)
    rec.Quality_Payment_Threshold__c = targets.qualityPaymentThreshold;

  // Diversity
  if (diversity.minimumContributors !== undefined)
    rec.Minimum_Contributors__c = diversity.minimumContributors;
  // Stats
  if (stats.totalApplied !== undefined)
    rec.Total_Applied__c = stats.totalApplied;
  if (stats.totalQualified !== undefined)
    rec.Total_Qualified__c = stats.totalQualified;
  if (stats.invitedAvailableContributors !== undefined)
    rec.Invited_Available_Contributors__c = stats.invitedAvailableContributors;
  if (stats.appReceived !== undefined) rec.App_Received__c = stats.appReceived;
  if (stats.matchedContributors !== undefined)
    rec.Matched_Contributors__c = stats.matchedContributors;
  if (stats.removed !== undefined) rec.Removed__c = stats.removed;
  if (stats.qualifiedContributors !== undefined)
    rec.Qualified_Contributors__c = stats.qualifiedContributors;
  if (stats.activeContributors !== undefined)
    rec.Active_Contributors__c = stats.activeContributors;
  if (stats.productionContributors !== undefined)
    rec.Production_Contributors__c = stats.productionContributors;
  // Rates
  if (rates.minimumRate !== undefined) rec.Minimum_Rate__c = rates.minimumRate;
  if (rates.nonUSFairpayRate !== undefined)
    rec.Non_US_Fairpay_Rate__c = rates.nonUSFairpayRate;
  if (rates.nonUSProjectPayRate !== undefined)
    rec.Non_US_Project_Pay_rate__c = rates.nonUSProjectPayRate;
  if (rates.projectRate !== undefined) rec.Project_Rate__c = rates.projectRate;
  if (rates.projectObjectivePayRate !== undefined)
    rec.Project_Objective_Pay_Rate__c = rates.projectObjectivePayRate;
  if (rates.projectPayRate !== undefined)
    rec.Project_Pay_Rate__c = rates.projectPayRate;
  if (rates.clientProjectTypePayRate !== undefined)
    rec.Client_Project_Type_Pay_Rate__c = rates.clientProjectTypePayRate;
  if (rates.clientPayRate !== undefined)
    rec.Client_Pay_Rate__c = rates.clientPayRate;
  if (rates.projectTypePayRate !== undefined)
    rec.Project_Type_Pay_Rate__c = rates.projectTypePayRate;
  if (rates.payRateAboveFairpay !== undefined)
    rec.Pay_Rate_Above_Fairpay__c = rates.payRateAboveFairpay;
  if (rates.flatPayRate !== undefined) rec.Flat_Pay_Rate__c = rates.flatPayRate;
  if (rates.projectIncentiveRate !== undefined)
    rec.Project_Incentive_Rate__c = rates.projectIncentiveRate;
  if (rates.maximumRate !== undefined) rec.Maximum_Rate__c = rates.maximumRate;
  if (rates.hidePayRate !== undefined) rec.Hide_Rate__c = rates.hidePayRate;

  // Legacy
  if (legacy.acProjectId !== undefined)
    rec.AC_Project_ID__c = legacy.acProjectId;
  if (legacy.eventLogsAcProjectId !== undefined)
    rec.Event_Logs_AC_Project_ID__c = legacy.eventLogsAcProjectId;
  if (legacy.rosterUserGroupIdExclusive !== undefined)
    rec.Roster_User_Group_ID_Exclusive__c = legacy.rosterUserGroupIdExclusive;
  if (legacy.rosterUserGroupId !== undefined)
    rec.Roster_User_Group_ID__c = legacy.rosterUserGroupId;
  if (legacy.rosterUserGroupName !== undefined)
    rec.Roster_User_Group_Name__c = legacy.rosterUserGroupName;
  // Admin
  if (admin.overrideDNH !== undefined)
    rec.Override_DNH__c = Boolean(admin.overrideDNH);
  if (admin.displayLeverPostErrorMessage !== undefined)
    rec.Display_Lever_Post_Error_Message__c = Boolean(
      admin.displayLeverPostErrorMessage,
    );
  if (admin.createInvites !== undefined)
    rec.Create_Invites__c = Boolean(admin.createInvites);
  if (admin.status !== undefined) rec.Status__c = admin.status || null;
  if (admin.countryLocale !== undefined)
    rec.Country_Locale__c = admin.countryLocale || null;
  if (admin.languageLocale !== undefined)
    rec.Language_Locale__c = admin.languageLocale || null;
  if (admin.dialectLocale !== undefined)
    rec.Dialect_Locale__c = admin.dialectLocale || null;
  if (admin.refresh !== undefined) rec.Refresh__c = Boolean(admin.refresh);
  if (admin.mercuryProjectObjectiveType !== undefined)
    rec.Mercury_Project_Objective_Type__c =
      admin.mercuryProjectObjectiveType || null;
  return rec;
}
